import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { AddCustomerDialog } from '@/components/customers/AddCustomerDialog';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { useCustomerSearch } from './customers/useCustomers';

/**
 * The customer book (M1-04).
 *
 * <b>Why this screen is in M1-04 at all.</b> M1-03 built the search — one box
 * across name, phone, email and every property address — and shipped it as an
 * endpoint with nothing calling it. M1-04's own AC is a detail page, and a
 * detail page with no list is reachable only by pasting a GUID into the
 * address bar. No later story owns a customer list either (the whole
 * Customers &amp; Properties epic is accounted for), so building the detail
 * page without this would have left both stories delivering nothing anyone
 * can navigate to.
 *
 * It is deliberately plain: a box, a table, and a link per row. Every matching
 * rule it appears to have is the API's — see `useCustomerSearch`.
 *
 * The term lives in the URL (`?q=`), like the dispatch board's date and view,
 * so a search is a link someone can send. It is written with `replace` while
 * typing: search-as-you-type would otherwise push one history entry per
 * keystroke and turn the Back button into a backspace key.
 */
export function Customers() {
  const { t } = useTranslation();
  const hasPermission = useHasPermission();
  const canManage = hasPermission(Permissions.manageCustomers);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTerm = searchParams.get('q') ?? '';

  const [showAddCustomer, setShowAddCustomer] = useState(false);
  // A polite live region rather than a toast, matching the detail page's
  // quick actions (M1-04): the new customer will not appear in THIS list
  // unless the search box happens to already match them, so the confirmation
  // has to be announced rather than assumed visible.
  const [announcement, setAnnouncement] = useState('');

  // Local state so the field stays responsive, with the URL following it.
  const [term, setTerm] = useState(urlTerm);

  // Re-seeded when the URL changes underneath us — Back, or a pasted link.
  // Guarded on inequality so this does not fight the user's own typing.
  useEffect(() => {
    setTerm((current) => (current === urlTerm ? current : urlTerm));
  }, [urlTerm]);

  useEffect(() => {
    if (term === urlTerm) {
      return;
    }
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (term.trim()) {
        next.set('q', term);
      } else {
        next.delete('q');
      }
      setSearchParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(timer);
  }, [term, urlTerm, searchParams, setSearchParams]);

  const customers = useCustomerSearch(urlTerm);
  const results = customers.data ?? [];

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('nav.customers')}</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">{t('customers.subtitle')}</p>
        </div>
        {canManage ? (
          <div className="flex items-center gap-3">
            {/* customers.manage, so a Dispatcher who can only READ the book
                does not get a link to a screen the API would refuse them —
                the same guard People.tsx puts on the skill catalog (M1-38). */}
            <Link to="/customers/lead-sources" className="text-sm underline underline-offset-2">
              {t('customers.manageLeadSources')}
            </Link>
            <Button type="button" size="sm" onClick={() => setShowAddCustomer(true)}>
              {t('customers.addCustomer.action')}
            </Button>
          </div>
        ) : null}
      </div>

      {/* A real <form>: Enter submits on a phone keyboard, which is where the
          "search" key otherwise does nothing. Submitting is a no-op beyond
          that — the debounce above has already run — so it only prevents the
          page reload. */}
      <form role="search" onSubmit={(event) => event.preventDefault()}>
        <div className="max-w-md text-sm">
          {/* `htmlFor`, not a wrapping <label>. A wrapper makes the field's
              accessible name its ENTIRE text content — the hint below would be
              read as part of the name ("Search customers Phone numbers match
              with or without formatting"), which is how a screen-reader user
              hears a label that is really two different things. The hint is
              `aria-describedby` instead: announced after the name, as a hint. */}
          <label htmlFor="customer-search" className="mb-1 block font-medium">
            {t('customers.searchLabel')}
          </label>
          <input
            id="customer-search"
            type="search"
            aria-describedby="customer-search-hint"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t('customers.searchPlaceholder')}
            className="h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-3 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          />
          <span
            id="customer-search-hint"
            className="mt-1 block text-xs text-[var(--color-muted-foreground)]"
          >
            {t('customers.searchHint')}
          </span>
        </div>
      </form>

      {customers.isPending ? (
        <LoadingState label={t('customers.loading')} />
      ) : customers.isError ? (
        <ErrorState
          title={t('customers.loadErrorTitle')}
          description={t('customers.loadErrorDescription')}
          onRetry={() => void customers.refetch()}
        />
      ) : results.length === 0 ? (
        // "No matches for that" and "no customers yet" are different facts, and
        // an office manager who cannot tell them apart will retype a search
        // that was never going to match anything.
        <EmptyState
          title={urlTerm ? t('customers.noMatchesTitle') : t('customers.emptyTitle')}
          description={
            urlTerm
              ? t('customers.noMatchesDescription', { term: urlTerm })
              : t('customers.emptyDescription')
          }
          action={
            !urlTerm && canManage ? (
              <Button type="button" size="sm" onClick={() => setShowAddCustomer(true)}>
                {t('customers.addCustomer.action')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {/* Announced, not just drawn: search-as-you-type changes this table
              under someone who may not be able to see it change. */}
          <p role="status" aria-live="polite" className="sr-only">
            {t('customers.resultCount', { count: results.length })}
          </p>

          <div
            role="region"
            aria-label={t('customers.tableRegion')}
            className="overflow-x-auto rounded-lg border"
            // WCAG 2.1.1 (M1-42).
            //
            // ⚠️ Measured in Chrome and it did NOT overflow, at 320, 640, 700 or
            // 768: 286px of content in 286px at the 320px reflow width, 606 in 606
            // at 640, 666 in 666 at 700. Contact drops out below `sm` and billing
            // city below `md`, and what is left wraps, so the `w-full` table
            // shrinks to fit rather than outrunning its container.
            //
            // So this is insurance, not a fix for something reproduced. Kept
            // because `overflow-x-auto` IS declared here and the container will
            // scroll the moment content does not fit — a column added, an
            // unbreakable token — and because the service-history table, which
            // looked equally safe, turned out to overflow by 33px at 320 once it
            // was actually measured.
            //
            // ⚠️ The honest cost meanwhile: one wasted tab stop. Chrome's own
            // auto-focusable-scroller behaviour applies only when a scroller
            // really overflows; a hard-coded `tabIndex` cannot tell the
            // difference.
            //
            // ⚠️ If it ever does overflow, the only focusable cell in a row is
            // the name link in the FIRST column — three of them at 320px, all on
            // the left — so tabbing would never drag this sideways by itself.
            //
            // Named, because a focusable region a screen reader cannot announce
            // is half a fix. `<div role="region">` rather than a `<section>` for
            // the lint reason recorded in eslint.config.js; the computed role is
            // the same. The table keeps its own <caption>: that describes the
            // contents, this names the thing you land on.
            tabIndex={0}
          >
            <table className="w-full text-sm">
              <caption className="sr-only">{t('customers.tableCaption')}</caption>
              <thead className="border-b bg-[var(--color-muted)]">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left font-medium">
                    {t('customers.columnName')}
                  </th>
                  <th scope="col" className="hidden px-4 py-2 text-left font-medium sm:table-cell">
                    {t('customers.columnContact')}
                  </th>
                  <th scope="col" className="hidden px-4 py-2 text-left font-medium md:table-cell">
                    {t('customers.columnBillingCity')}
                  </th>
                  <th scope="col" className="px-4 py-2 text-left font-medium">
                    {t('customers.columnStatus')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.map((customer) => (
                  <tr key={customer.id} className="border-b last:border-b-0">
                    <td className="px-4 py-2">
                      <Link
                        to={`/customers/${customer.id}`}
                        className="font-medium underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                      >
                        {customer.name}
                      </Link>
                      {/* The contact column is hidden on a phone, so keep it
                          reachable rather than merely invisible. */}
                      <span className="block text-xs text-[var(--color-muted-foreground)] sm:hidden">
                        {customer.phone ?? customer.email ?? ''}
                      </span>
                    </td>
                    <td className="hidden px-4 py-2 text-[var(--color-muted-foreground)] sm:table-cell">
                      {customer.phone ?? customer.email ?? '—'}
                    </td>
                    <td className="hidden px-4 py-2 text-[var(--color-muted-foreground)] md:table-cell">
                      {customer.billingAddress.city}
                    </td>
                    <td className="px-4 py-2">
                      {t(`customers.status.${customer.status}`, { defaultValue: customer.status })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p role="status" aria-live="polite" className="sr-only" data-testid="customers-announcement">
        {announcement}
      </p>

      {showAddCustomer ? (
        <AddCustomerDialog
          onClose={() => setShowAddCustomer(false)}
          onAdded={(name, duplicateNameWarning) =>
            setAnnouncement(
              duplicateNameWarning
                ? t('customers.addCustomer.addedWithDuplicateWarning', { name })
                : t('customers.addCustomer.added', { name }),
            )
          }
        />
      ) : null}
    </section>
  );
}
